import {
  MAX_CLOUD_IMAGE_SIZE,
  ProcessTypes,
  SCENE7_TEMPLATE_BASIC_URL_FOR_SAVEDART,
  SCENE7_TEMPLATE_BASIC_URL_STATIC,
  SCENE7_URL_BASE,
  UNDERSCORE,
  bomBackgroundColor,
  bomOptionType,
  imprintOptionType,
  nucleusHostName,
  previewType,
  userImageType,
} from '../utils/constants';
import {
  changeColorBrightness,
  getHotStampedScene7Url,
  getLighterColorHexForDebossedFullPreview,
  getPreviewDimension,
  getStockImageUrl,
  getStockImageUrlByExistenceMode,
  strictEncode,
} from '../utils/scene7url';
import {
  checkShowImageSingleColor,
  getAllAppliedImprintAreaKeys,
  getAllSelectedImprintAreaKeys,
  getCurrentImprintFormat,
  getDebossColorReference,
  getImprintOptionType,
} from '../imprintarea/selectors';
import { getBomOptionsForFullPreview, getBomOptionsForLitePreview } from '../bom/selectors';
import {
  getCurrentImprintAreaKey,
  getCurrentImprintProcessType,
  getItemId,
  getItemUseScene7Template,
  getStockImageTypeDict,
  getStudioItemType,
  getTwinItemId,
} from '../main/selectors';
import { promoStudioUITypeValue, studioItemType as studioItemTypeEnum } from '../main/constants';

import { SelectionType } from '../bom/constants';
import { calculateDebossColor } from '../imprintarea/logics';
import { createSelector } from 'reselect';
import { getCurrentBomOption } from '../nextgen/product/selectors';
import { getIsNextGenUI } from '../nextgen/selectors';
import { getPromoStudioUIType } from '../lite/selectors';
import { processUrls } from './logics';
import { v4 as uuidv4 } from 'uuid';

export const getScene7Urls = createSelector(
  (state) => state.ui.scene7Urls,
  (scene7Urls) => scene7Urls
);
export const getSelectedScene7Url = createSelector(
  (state) => state.ui.scene7Urls.find((scene7Url) => scene7Url.isSelected),
  (scene7Url) => scene7Url
);
const getCurrentMoreViewIndexInfo = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.ui.moreviews,
  (currentImprintAreaKey, moreviews) => {
    let currentMoreViewIndexInfo = moreviews[currentImprintAreaKey] || { previewIndex: 0, colorIndex: 0 };
    return currentMoreViewIndexInfo;
  }
);

export const getBomColorsInfo = createSelector(
  getBomOptionsForFullPreview,
  getBomOptionsForLitePreview,
  getPromoStudioUIType,
  getCurrentMoreViewIndexInfo,
  (state) => state.ui.moreviewsLite,
  (state) => state.questions,
  (fullBomOptions, liteBomOptions, uiType, moreViewIndexInfo, moreviewsLite, questions) => {
    const isLite = uiType === promoStudioUITypeValue.LiteUI;
    const bomOptions = isLite ? liteBomOptions : fullBomOptions;
    const keys = Object.keys(bomOptions);
    if (keys.length > 1) {
      let bomColors = [];
      keys.forEach((key) => {
        bomColors = bomColors.concat(bomOptions[key]);
      });
      return {
        bomOptionType: bomOptionType.Multiple,
        bomColors,
        selectedIndex: 0,
      };
    } else if (keys.length === 1) {
      let bomColors = bomOptions[keys[0]];
      return {
        bomOptionType: bomOptionType.Single,
        bomColors,
        selectedIndex: isLite
          ? questions.length > 0 &&
            questions[0].selectionType === SelectionType.MULTIPLE &&
            bomColors &&
            bomColors.length > 1
            ? moreviewsLite.colorIndex
            : 0
          : moreViewIndexInfo.colorIndex,
      };
    }
    return null;
  }
);

export const getCurrentSelectedBomColorInfo = createSelector(getBomColorsInfo, (bomColorsInfo) => {
  return bomColorsInfo ? bomColorsInfo.bomColors[bomColorsInfo.selectedIndex] : null;
});
export const replaceDesignPreviewUrlPlaceholder = (designPreview, previewColor) => {
  let url = designPreview.url;
  const productColor = previewColor.replace('#', '');

  const backgroundColorPlaceHolder = '&color=(_.-!BGC!-._)';
  if (url.includes(backgroundColorPlaceHolder)) {
    url = url.replace(backgroundColorPlaceHolder, productColor ? `&color=${productColor}` : '');
  }

  const debossPlaceHolder = '(_.-!Deboss!-._)';
  if (url.includes(debossPlaceHolder)) {
    const debossColor = calculateDebossColor(productColor);
    url = url.replace(debossPlaceHolder, debossColor);
  }
  return url;
};
export const replaceFullPreviewUrlPlaceholder = function (fullPreview, bomOptions) {
  let url = fullPreview.url;
  let bomColorCode = fullPreview.placeHolder.bomColorCode;
  const debossCode = fullPreview.placeHolder.deboss.code;
  let leftPlaceHolder = fullPreview.placeHolder.leftPlaceHolder;
  let rightPlaceHolder = fullPreview.placeHolder.rightPlaceHolder;
  let texturePlaceHolder = '';
  Object.keys(bomColorCode).forEach((key) => {
    const code = key;
    let placeHolder = leftPlaceHolder + code + rightPlaceHolder;
    let bomOption = bomOptions.find((o) => o.code === code);
    let color = '';
    if (bomOption !== undefined) {
      if (bomOption.useTexture) {
        color = bomOption.code + UNDERSCORE + bomOption.codeValue;
        texturePlaceHolder = bomColorCode[key].textureUrl.encodedUrl;
      } else {
        color = bomOption.colorReference.replace('#', '');
        texturePlaceHolder = bomColorCode[key].nonTextureUrl.encodedUrl;
      }
      url = url.replace(placeHolder, texturePlaceHolder).replace(placeHolder, color);
    }
  });
  if (debossCode !== null) {
    const placeHolder = leftPlaceHolder + fullPreview.placeHolder.deboss.placeHolder + rightPlaceHolder;
    const bomOption = bomOptions.find((o) => o.code === debossCode);
    if (bomOption !== undefined) {
      const colorReference = bomOption.colorReference;
      const color = getLighterColorHexForDebossedFullPreview(colorReference);
      const placeHolderEncode = strictEncode(placeHolder);
      url = url.replace(placeHolderEncode, color.replace('#', ''));
    }
  }
  return url;
};

export const getFullPreviewUrlsFactory = function (
  imprintAreaKey,
  bomOptions,
  fullPreviewDict,
  currentOptionType,
  hasText,
  previewColor
) {
  let fullPreviewUrls = fullPreviewDict[imprintAreaKey];
  let previewUrls = { previews: [], thumbnails: [] };
  if (!fullPreviewUrls) {
    return previewUrls;
  }
  for (let fullPreview of fullPreviewUrls) {
    let defaultWidth = fullPreview.defaultWidth;
    let defaultHeight = fullPreview.defaultHeight;
    let isFullVignette = fullPreview.isFullVignette;
    let isDefault = fullPreview.isDefault;
    let type = fullPreview.type;
    let previewUrl = {
      imprintAreaKey: imprintAreaKey,
      defaultWidth: defaultWidth,
      defaultHeight: defaultHeight,
      isFullVignette: isFullVignette,
      type: type,
      url: '',
      isDefault: isDefault,
    };
    let thumbnailUrl = {
      imprintAreaKey: imprintAreaKey,
      defaultWidth: defaultWidth,
      defaultHeight: defaultHeight,
      isDefault: isDefault,
      isFullVignette: isFullVignette,
      type: type,
      url: '',
    };
    let url = '';
    if (type === previewType.Vignette) {
      url = replaceFullPreviewUrlPlaceholder(fullPreview, bomOptions);
    } else {
      url = replaceDesignPreviewUrlPlaceholder(fullPreview, previewColor);
    }
    previewUrl.url = url + '&scl=1';
    previewUrl.label = fullPreview.label;
    // Square thumbnail
    let thumbnailWidth = 140;
    thumbnailUrl.url = url + '&wid=' + Math.round(thumbnailWidth) + '&hei=' + Math.round(thumbnailWidth);
    thumbnailUrl.label = fullPreview.label;

    // if the imprint area only allow 'Text Only', in the meantime, there is no any text line for this imprint area, only Verse/Sentiment/Outer Slogan, then not display the empty imprint view
    if (!(currentOptionType === imprintOptionType.TextOnly && !hasText && fullPreview.type === previewType.Fxg)) {
      previewUrls.previews.push(previewUrl);
      previewUrls.thumbnails.push(thumbnailUrl);
    }
  }

  return previewUrls;
};

export const getSelectedColorBomOptions = createSelector(
  getBomColorsInfo,
  getCurrentMoreViewIndexInfo,
  getIsNextGenUI,
  getCurrentBomOption,
  (bomColorsInfo, moreViewIndexInfo, isNextGenUI, currentBomOption) => {
    let bomOptions = [];
    if (bomColorsInfo && bomColorsInfo.bomColors) {
      if (bomColorsInfo.bomOptionType === bomOptionType.Multiple) {
        bomOptions = bomColorsInfo.bomColors;
      } else {
        if (bomColorsInfo.bomColors.length > 0) {
          if (!isNextGenUI) {
            bomOptions = [bomColorsInfo.bomColors[moreViewIndexInfo.colorIndex]];
          } else {
            if (currentBomOption) {
              bomOptions = [currentBomOption];
            }
          }
        }
      }
    }
    return bomOptions;
  }
);

export const getLiteSelectedColorBomOptions = createSelector(
  getBomColorsInfo,
  (state) => state.ui.moreviewsLite,
  (state) => state.questions,
  (bomColorsInfo, moreviewsLite, questions) => {
    let bomOptions = [];
    if (bomColorsInfo && bomColorsInfo.bomColors) {
      if (bomColorsInfo.bomOptionType === bomOptionType.Multiple) {
        bomOptions = bomColorsInfo.bomColors;
      } else {
        if (bomColorsInfo.bomColors.length > 0) {
          bomOptions = [
            bomColorsInfo.bomColors[
              questions.length > 0 &&
              questions[0].selectionType === SelectionType.MULTIPLE &&
              bomColorsInfo.bomColors.length > 1
                ? moreviewsLite.colorIndex
                : 0
            ],
          ];
        }
      }
    }
    return bomOptions;
  }
);

export const getProductPreviews = createSelector(
  (state, imprintAreaKey) => imprintAreaKey || getCurrentImprintAreaKey(state),
  getSelectedColorBomOptions,
  getCurrentMoreViewIndexInfo,
  (state) => state.ui.fullPreviewDict,
  (state) => state.textDict[getCurrentImprintAreaKey(state)] || [],
  (state) => getImprintOptionType(state, getCurrentImprintAreaKey(state)),
  (state, imprintAreaKey) => getPreviewColor(state, imprintAreaKey),
  (
    currentImprintAreaKey,
    bomOptions,
    moreViewIndexInfo,
    fullPreviewDict,
    textList,
    currentOptionType,
    previewColor
  ) => {
    let hasText = textList.length !== 0;
    let productPreviews = getFullPreviewUrlsFactory(
      currentImprintAreaKey,
      bomOptions,
      fullPreviewDict,
      currentOptionType,
      hasText,
      previewColor
    );
    let processUrl = { previewUrl: null, thumbnailUrl: null };
    processUrl.previewUrl = processUrls('preview', moreViewIndexInfo, productPreviews.previews);
    processUrl.thumbnailUrl = processUrls('preview', moreViewIndexInfo, productPreviews.thumbnails);
    return processUrl;
  }
);

export const getLiteProductPreviews = createSelector(
  getAllSelectedImprintAreaKeys,
  getLiteSelectedColorBomOptions,
  (state) => state.ui.moreviewsLite,
  (state) => state.ui.fullPreviewDict,
  (state) => state.textDict,
  (state) => state,
  (selectedImprintAreaKeys, bomOptions, moreviews, fullPreviewDict, textDict, state) => {
    let processUrl = { previewUrl: [], thumbnailUrl: [] };
    for (let selectedImprintAreaKey of selectedImprintAreaKeys) {
      const previewColor = getPreviewColor(state, selectedImprintAreaKey);
      const currentOptionType = getImprintOptionType(state, selectedImprintAreaKey);
      let hasText = textDict[selectedImprintAreaKey] && textDict[selectedImprintAreaKey].length !== 0;

      const urls = getFullPreviewUrlsFactory(
        selectedImprintAreaKey,
        bomOptions,
        fullPreviewDict,
        currentOptionType,
        hasText,
        previewColor
      );
      processUrl.previewUrl = processUrl.previewUrl.concat(urls.previews);
      processUrl.thumbnailUrl = processUrl.thumbnailUrl.concat(urls.thumbnails);
    }
    processUrl.previewUrl = processUrls('preview', moreviews, processUrl.previewUrl);
    processUrl.thumbnailUrl = processUrls('preview', moreviews, processUrl.thumbnailUrl);
    return processUrl;
  }
);

export const getLiteHasVignette = createSelector(
  getAllSelectedImprintAreaKeys,
  (state) => state.imprintAreaDict,
  (selectedImprintAreaKeys, imprintAreaDict) => {
    let hasVignette = false;
    for (let selectedImprintAreaKey of selectedImprintAreaKeys) {
      if (imprintAreaDict[selectedImprintAreaKey] && imprintAreaDict[selectedImprintAreaKey].vignettes.length > 0) {
        hasVignette = true;
      }
    }
    return hasVignette;
  }
);

export const getFirstProductPreview = createSelector(getProductPreviews, (productPreviews) => {
  if (productPreviews.previewUrl.length > 0) {
    let processUrlFullVignette = productPreviews.previewUrl.find((preview) => preview.isFullVignette);
    if (processUrlFullVignette) {
      return processUrlFullVignette;
    } else {
      return productPreviews.previewUrl[0];
    }
  }
  return null;
});

export const getProductPreview = createSelector(getProductPreviews, (productPreviews) => {
  if (productPreviews) {
    return productPreviews.previewUrl.find((preview) => preview.isSelected);
  }
  return null;
});

export const getLiteProductPreview = createSelector(getLiteProductPreviews, (liteProductPreviews) => {
  if (liteProductPreviews) {
    return liteProductPreviews.previewUrl.find((preview) => preview.isSelected);
  }
  return null;
});

export const getColorPreviews = createSelector(
  getCurrentImprintAreaKey,
  getBomColorsInfo,
  getCurrentMoreViewIndexInfo,
  (state) => state.ui.fullPreviewDict,
  (state) => state.textDict[getCurrentImprintAreaKey(state)] || [],
  (state) => getImprintOptionType(state, getCurrentImprintAreaKey(state)),
  (state) => getPreviewColor(state, getCurrentImprintAreaKey(state)),
  (
    currentImprintAreaKey,
    bomColorsInfo,
    moreViewIndexInfo,
    fullPreviewDict,
    textList,
    currentOptionType,
    previewColor
  ) => {
    let colorPreviews = [];
    let bomOptions = null;
    let hasText = textList.length !== 0;
    if (bomColorsInfo && bomColorsInfo.bomColors) {
      if (bomColorsInfo.bomOptionType === bomOptionType.Multiple) {
        bomOptions = bomColorsInfo.bomColors;
        let previewUrls = getFullPreviewUrlsFactory(
          currentImprintAreaKey,
          bomOptions,
          fullPreviewDict,
          currentOptionType,
          hasText,
          previewColor
        );
        colorPreviews.push(previewUrls.previews);
      } else {
        bomColorsInfo.bomColors.forEach((bomColor) => {
          bomOptions = [bomColor];
          let previewUrls = getFullPreviewUrlsFactory(
            currentImprintAreaKey,
            bomOptions,
            fullPreviewDict,
            currentOptionType,
            hasText,
            previewColor
          );
          colorPreviews.push(previewUrls.previews);
        });
      }
      //use the first one as the color preview
      colorPreviews = colorPreviews.map((item) => item[0]);
      return processUrls('color', moreViewIndexInfo, colorPreviews);
    } else {
      return null;
    }
  }
);

export const getAllSelectedPreviews = createSelector(
  getAllAppliedImprintAreaKeys,
  getAllSelectedImprintAreaKeys,
  getStudioItemType,
  getBomColorsInfo,
  (state) => state.ui.fullPreviewDict,
  (state) => state.textDict,
  (state) => state,
  (
    allSelectedCustomizedImprintAreaKey,
    allSelectedImprintAreas,
    studioItemType,
    bomColorsInfo,
    fullPreviewDict,
    textDict,
    state
  ) => {
    let previews = [];
    allSelectedImprintAreas.forEach((currentImprintAreaKey) => {
      let bomOptions = [];
      let previewsUrl = [];
      const previewColor = getPreviewColor(state, currentImprintAreaKey);
      const currentOptionType = getImprintOptionType(state, currentImprintAreaKey);
      let hasText = textDict[currentImprintAreaKey] && textDict[currentImprintAreaKey].length !== 0;

      if (bomColorsInfo && bomColorsInfo.bomColors) {
        if (bomColorsInfo.bomOptionType === bomOptionType.Multiple) {
          bomOptions = bomColorsInfo.bomColors;
          let productPreviews = getFullPreviewUrlsFactory(
            currentImprintAreaKey,
            bomOptions,
            fullPreviewDict,
            currentOptionType,
            hasText,
            previewColor
          );
          previews = previews.concat(productPreviews.previews);
        } else {
          bomColorsInfo.bomColors.forEach((bomColor) => {
            let productPreviews = getFullPreviewUrlsFactory(
              currentImprintAreaKey,
              [bomColor],
              fullPreviewDict,
              currentOptionType,
              hasText,
              previewColor
            );
            previewsUrl = previewsUrl.concat(productPreviews.previews);
          });
          let hasFxg = previewsUrl.some((p) => p.type === previewType.Fxg);
          if (hasFxg) {
            const emailPreviewsHasVignette = previewsUrl.filter((p) => p.type === previewType.Vignette);
            const firstEmailPreviewHasFxg = previewsUrl.find((p) => p.type === previewType.Fxg);
            previews = previews.concat(emailPreviewsHasVignette.concat(firstEmailPreviewHasFxg));
          } else {
            previews = previews.concat(previewsUrl);
          }
        }
      } else {
        let productPreviews = getFullPreviewUrlsFactory(
          currentImprintAreaKey,
          bomOptions,
          fullPreviewDict,
          currentOptionType,
          hasText,
          previewColor
        );
        previews = previews.concat(productPreviews.previews);
      }
    });

    //Remove uncustomized fxg previews in screen printed/digital
    if (studioItemType !== studioItemTypeEnum.nonDigital)
      previews = previews.filter(
        (preview) =>
          preview.type === previewType.Vignette ||
          (preview.type === previewType.Fxg && allSelectedCustomizedImprintAreaKey.includes(preview.imprintAreaKey))
      );
    return previews;
  }
);

export const getPrintPreviews = createSelector(
  getAllAppliedImprintAreaKeys,
  getAllSelectedImprintAreaKeys,
  getStudioItemType,
  getBomColorsInfo,
  (state) => state.ui.fullPreviewDict,
  (state) => state.textDict,
  (state) => state,
  getIsNextGenUI,
  (
    allSelectedCustomizedImprintAreaKey,
    allSelectedImprintAreaKeys,
    studioItemType,
    bomColorsInfo,
    fullPreviewDict,
    textDict,
    state,
    isNextGenUI
  ) => {
    let printPreviews = [];
    let productPreviews = [];
    // the previews for the same bom color has the same identifier
    let identifiers = [];
    allSelectedImprintAreaKeys.forEach((currentImprintAreaKey) => {
      let bomOptions = [];
      const previewColor = getPreviewColor(state, currentImprintAreaKey);
      const currentOptionType = getImprintOptionType(state, currentImprintAreaKey);
      let hasText = textDict[currentImprintAreaKey] && textDict[currentImprintAreaKey].length !== 0;

      if (bomColorsInfo && bomColorsInfo.bomColors) {
        // may has multiple bom colors
        if (bomColorsInfo.bomOptionType === bomOptionType.Multiple) {
          // multiple type bom color, get the product previews directly from fullPreviewDict
          bomOptions = bomColorsInfo.bomColors;
          let previewUrls = getFullPreviewUrlsFactory(
            currentImprintAreaKey,
            bomOptions,
            fullPreviewDict,
            currentOptionType,
            hasText,
            previewColor
          );
          productPreviews = previewUrls.previews;
          const identifier = 'Multi-Color';
          if (!identifiers.some((i) => i === identifier)) {
            identifiers.push(identifier);
          }
          printPreviews.push({
            identifier,
            label: 'Product Preview',
            previews: productPreviews,
          });
        } else {
          // regular type of bom color, we should get multiple sets of previews when multiple colors are selected
          bomColorsInfo.bomColors.forEach((bomColor) => {
            const identifier = `${bomColor.code}-${bomColor.codeValue}`;
            if (!identifiers.some((i) => i === identifier)) {
              identifiers.push(identifier);
            }
            let previewUrls = getFullPreviewUrlsFactory(
              currentImprintAreaKey,
              [bomColor],
              fullPreviewDict,
              currentOptionType,
              hasText,
              previewColor
            );
            productPreviews = previewUrls.previews;
            // if only one color is selected, use 'Product Preview' as the label(without color name)
            let label =
              bomColorsInfo.bomColors.length > 1 ? `Product Preview in ${bomColor.description}` : 'Product Preview';
            printPreviews.push({
              identifier,
              label,
              previews: productPreviews,
            });
          });
        }
      } else {
        // bom color is not available, get the product previews directly from fullPreviewDict
        let previewUrls = getFullPreviewUrlsFactory(
          currentImprintAreaKey,
          bomOptions,
          fullPreviewDict,
          currentOptionType,
          hasText,
          previewColor
        );
        productPreviews = previewUrls.previews;
        const identifier = 'Multi-Color';
        if (!identifiers.some((i) => i === identifier)) {
          identifiers.push(identifier);
        }
        printPreviews.push({
          identifier,
          label: 'Product Preview',
          previews: productPreviews,
        });
      }
    });

    // printPreviews structures:
    // [
    //   { identifier: 'some-id1', label: 'Product Preview in Red', previews: [urlObj1, urlObj2] }, // for side 1
    //   { identifier: 'some-id2', label: 'Product Preview in Green', previews: [urlObj1, urlObj2] }, // for side 1
    //   { identifier: 'some-id1', label: 'Product Preview in Red', previews: [urlObj1, urlObj2] }, // for side 2
    //   { identifier: 'some-id2', label: 'Product Preview in Green', previews: [urlObj1, urlObj2] }, // for side 2
    // ];

    // const printOptions = [
    //   { identifier: 'some-id1', label: 'Product Preview in Green', type: previewType.Vignette, previews: [urlObj1, urlObj2] },
    //   { identifier: 'some-id2', label: 'Product Preview in Blue', type: previewType.Vignette, previews: [urlObj1, urlObj2] },
    //   { identifier: 'some-id3', label: 'Imprint Preview', type: previewType.Fxg, previews: [urlObj1, urlObj2] }
    // ];

    let printOptions = [];
    if (printPreviews.length > 0) {
      //Remove uncustomized fxg previews in screen printed/digital
      printPreviews.forEach((printPreview) =>
        printPreview.previews.forEach((preview, index) => {
          if (
            studioItemType !== studioItemTypeEnum.nonDigital &&
            preview.type === previewType.Fxg &&
            !allSelectedCustomizedImprintAreaKey.includes(preview.imprintAreaKey)
          ) {
            printPreview.previews.splice(index, 1);
          }
        })
      );
      // check if there is any vignette or fxg
      const hasVignette = printPreviews.some((printPreview) =>
        printPreview.previews.some((preview) => preview.type === previewType.Vignette)
      );
      const hasFxg = printPreviews.some((printPreview) =>
        printPreview.previews.some((preview) => preview.type === previewType.Fxg)
      );

      if (hasVignette) {
        identifiers.forEach((identifier) => {
          // get all previews for current identifier(color)
          const previewsForCurrentIdentifier = printPreviews.filter(
            (printPreview) => printPreview.identifier === identifier
          );
          // combind the previews to one for current identifier(same color for multiple sides)
          const vignettePreviews = previewsForCurrentIdentifier.reduce((prev, cur) => {
            return prev.concat(cur.previews.filter((p) => p.type === previewType.Vignette));
          }, []);
          // use the first label
          const label = previewsForCurrentIdentifier[0].label;
          //add to the print options list
          printOptions.push({
            identifier,
            label,
            type: previewType.Vignette,
            previews: vignettePreviews,
          });
        });
      }
      if (hasFxg) {
        // we can just use the first identifier(color)
        const firstIdentifier = identifiers[0];
        const previewsForCurrentIdentifier = printPreviews.filter(
          (printPreview) => printPreview.identifier === firstIdentifier
        );
        const fxgPreviews = previewsForCurrentIdentifier.reduce((prev, cur) => {
          return prev.concat(cur.previews.filter((p) => p.type === previewType.Fxg));
        }, []);
        printOptions.push({
          identifier: 'fxg',
          label: isNextGenUI ? 'Design Only Preview' : 'Imprint Preview',
          type: previewType.Fxg,
          previews: fxgPreviews,
        });
      }
    }

    return printOptions;
  }
);

export const getNextGenUIPreviewColor = createSelector(
  (state) => state.imprintAreaDict,
  (state, formatKey) => formatKey || getCurrentImprintAreaKey(state),
  getBomColorsInfo,
  getCurrentBomOption,
  (state) => state.questions,
  (imprintAreaDict, imprintAreaKey, bomColorsInfo, currentBomOption, questions) => {
    if (imprintAreaKey && imprintAreaDict[imprintAreaKey]) {
      const currentImprintFormat = imprintAreaDict[imprintAreaKey];
      if (currentImprintFormat) {
        if (currentImprintFormat.mandatoryMapBomColor === bomBackgroundColor.mandatoryNot) {
          return '';
        } else {
          let bomOption = undefined;
          if (bomColorsInfo && bomColorsInfo.bomColors) {
            if (currentImprintFormat.mandatoryMapBomColor === bomBackgroundColor.auto) {
              if (bomColorsInfo.bomOptionType === bomOptionType.Multiple) {
                const colorCode = bomColorsInfo.bomColors[0]?.code;
                const isMultiple =
                  colorCode && questions.find((q) => q.code === colorCode)?.selectionType === SelectionType.MULTIPLE;

                bomOption = isMultiple && currentBomOption ? currentBomOption : bomColorsInfo.bomColors[0];
              } else {
                bomOption = currentBomOption;
              }
            } else if (currentImprintFormat.mandatoryMapBomColor === bomBackgroundColor.mandatory) {
              let bomOptions = bomColorsInfo.bomColors.filter((o) => o.code === currentImprintFormat.bomColorCode);
              if (bomColorsInfo.bomOptionType === bomOptionType.Multiple) {
                const isMultiple =
                  questions.find((q) => q.code === currentImprintFormat.bomColorCode)?.selectionType ===
                  SelectionType.MULTIPLE;

                bomOption = isMultiple && currentBomOption ? currentBomOption : bomOptions[0];
              } else {
                bomOption = currentBomOption;
              }
            }
          }
          return bomOption && bomOption.colorReference ? bomOption.colorReference : '';
        }
      }
    }
  }
);

export const getFullUIPreviewColor = createSelector(
  getBomColorsInfo,
  getCurrentMoreViewIndexInfo,
  getCurrentImprintFormat,
  (bomColorsInfo, moreViewIndexInfo, currentImprintFormat) => {
    if (currentImprintFormat) {
      if (currentImprintFormat.mandatoryMapBomColor === bomBackgroundColor.mandatoryNot) {
        return '';
      } else {
        let bomOption = undefined;
        if (bomColorsInfo && bomColorsInfo.bomColors) {
          if (currentImprintFormat.mandatoryMapBomColor === bomBackgroundColor.auto) {
            if (bomColorsInfo.bomOptionType === bomOptionType.Multiple) {
              bomOption = bomColorsInfo.bomColors.find((_, index) => index === moreViewIndexInfo.colorIndex);
            } else {
              bomOption = bomColorsInfo.bomColors[moreViewIndexInfo.colorIndex];
            }
          } else if (currentImprintFormat.mandatoryMapBomColor === bomBackgroundColor.mandatory) {
            let bomOptions = bomColorsInfo.bomColors.filter((o) => o.code === currentImprintFormat.bomColorCode);
            bomOption = bomOptions[moreViewIndexInfo.colorIndex];
          }
        }
        return bomOption && bomOption.colorReference ? bomOption.colorReference : '';
      }
    }
  }
);
export const getPreviewColor = (state, imprintAreaKey) => {
  const isNextGenUI = getIsNextGenUI(state);
  return isNextGenUI ? getNextGenUIPreviewColor(state, imprintAreaKey) : getFullUIPreviewColor(state, imprintAreaKey);
};
export const getPreviewColorForVignette = createSelector(
  getBomColorsInfo,
  getCurrentMoreViewIndexInfo,
  getCurrentImprintFormat,
  (bomColorsInfo, moreViewIndexInfo, currentImprintFormat) => {
    if (currentImprintFormat) {
      let bomOption;
      if (bomColorsInfo && bomColorsInfo.bomColors) {
        let bomOptions = bomColorsInfo.bomColors.filter((o) => o.code === currentImprintFormat.bomColorCode);
        bomOption = bomOptions[moreViewIndexInfo.colorIndex];
        if (!bomOption && bomColorsInfo.bomColors && bomColorsInfo.bomColors.length > 0)
          bomOption = bomColorsInfo.bomColors[0];
      }
      return bomOption && bomOption.colorReference ? bomOption.colorReference : '';
    }
  }
);

export const getImprintViewPreview = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.imprintAreaDict,
  (state) => state.ui.fullPreviewDict,
  (_, bomOptions) => bomOptions,
  (state, bomOptions) => getStockImage(state, bomOptions),
  (currentImprintAreaKey, imprintAreaDict, fullPreviewDict, bomOptions, stockImageUrl) => {
    let preview = null;
    let urlWidth = '&wid=800';
    preview = { url: '', type: undefined };
    let fullPreviewUrls = fullPreviewDict[currentImprintAreaKey];
    const hasVignette =
      imprintAreaDict[currentImprintAreaKey] && imprintAreaDict[currentImprintAreaKey].vignettes.length > 0;
    if (hasVignette) {
      if (fullPreviewUrls) {
        let fullVignetteUrl = fullPreviewUrls.find(
          (x) => x.type === previewType.Vignette && x.isDefault && x.isFullVignette
        )
          ? fullPreviewUrls.find((x) => x.type === previewType.Vignette && x.isDefault && x.isFullVignette)
          : fullPreviewUrls.find((x) => x.type === previewType.Vignette && x.isFullVignette);
        if (fullVignetteUrl !== undefined) {
          preview.url = replaceFullPreviewUrlPlaceholder(fullVignetteUrl, bomOptions) + urlWidth;
        } else {
          let basicVignetteUrl = fullPreviewUrls.find((x) => x.type === previewType.Vignette && !x.isFullVignette);
          if (basicVignetteUrl !== undefined) {
            preview.url = replaceFullPreviewUrlPlaceholder(basicVignetteUrl, bomOptions) + urlWidth;
          }
        }
      } else {
        preview.url = '';
      }
      preview.type = previewType.Vignette;
    } else {
      if (stockImageUrl && stockImageUrl.url) {
        preview.url = stockImageUrl.url;
        preview.type = stockImageUrl.type;
      } else {
        let fxgUrl = fullPreviewUrls && fullPreviewUrls.find((x) => x.type === previewType.Fxg);
        preview.url = fxgUrl === undefined ? '' : fxgUrl.url + urlWidth;
        preview.type = previewType.Fxg;
      }
    }
    // if the urls are the same, the browser will have a cache and the images will not be reloaded
    if (preview.url !== '') {
      preview.url += '&guid=' + uuidv4();
    }
    return preview;
  }
);

export const getStockImage = createSelector(
  getItemId,
  getTwinItemId,
  getStockImageTypeDict,
  (_, bomOptions) => bomOptions,
  (state) => state.specs.stockImageExist,
  (itemId, twinItemId, stockImageTypeDict, bomOptions, stockImageExist) => {
    let preview = { url: '', type: undefined };
    // Only monochrome products support multiple types of stock images.
    if (bomOptions.length === 1) {
      preview.url = getStockImageUrlByExistenceMode(
        itemId,
        twinItemId,
        stockImageTypeDict,
        bomOptions[0],
        stockImageExist
      );
    } else {
      preview.url = stockImageExist && getStockImageUrl(itemId);
    }
    preview.type = previewType.StockImage;
    return preview;
  }
);

export const getLitePreviewUrl = createSelector(
  getLiteProductPreview,
  getLiteHasVignette,
  (state, bomOptions) => getStockImage(state, bomOptions),
  (liteProductPreview, hasVignette, stockImageUrl) => {
    let litePreviewUrl = hasVignette ? liteProductPreview && liteProductPreview.url : stockImageUrl.url;
    // if the urls are the same, the browser will have a cache and the images will not be reloaded
    if (litePreviewUrl !== '') {
      litePreviewUrl += '&guid=' + uuidv4();
    }
    return litePreviewUrl;
  }
);

export const getImagePreviewUrl = createSelector(
  (_, image) => image,
  (state) => getCurrentImprintProcessType(state),
  (state, image) => checkShowImageSingleColor(state, image),
  getBomColorsInfo,
  getCurrentImprintAreaKey,
  getItemUseScene7Template,
  (state) => state.ui.fullPreviewDict,
  (state) => getDebossColorReference(state),
  (_, image, extraParam) => extraParam,
  (_, image, widthRequired) => widthRequired,
  (
    image,
    processType,
    isColorized,
    bomOptions,
    currentImprintAreaKey,
    itemUseScene7Template,
    fullPreview,
    debossColorReference,
    extraParam,
    widthRequired
  ) => {
    if (!image) return '';

    const PLACEHOLDER_SCENE7FILENAME = '{Scene7FileName}';
    let url = SCENE7_TEMPLATE_BASIC_URL_STATIC.replace(PLACEHOLDER_SCENE7FILENAME, image.scene7FileName);
    if (itemUseScene7Template) {
      if (image.type === userImageType.UploadLogo) url = SCENE7_URL_BASE;
      else if (image.type === userImageType.SavedArt)
        url = SCENE7_TEMPLATE_BASIC_URL_FOR_SAVEDART.replace(PLACEHOLDER_SCENE7FILENAME, image.scene7FileName);
    } else if (image.type === userImageType.UploadLogo || image.type === userImageType.SavedArt) {
      const ART_URL = `${
        nucleusHostName[process.env.REACT_APP_BUILD_ENV]
      }/api/v1/entity/Promo/content/{Scene7FileName}/image?`;
      url = ART_URL.replace(PLACEHOLDER_SCENE7FILENAME, image.scene7FileName);
    }

    if (image.type === userImageType.SavedArt && !image.scene7FileName) {
      return image.thumbnail;
    }
    if (image.type === userImageType.UploadLogo && extraParam.forRecent) {
      url += getUploadLogoDefaultScene7TemplateUrl(image, widthRequired);
      return url;
    }
    const hasImageColors = image.colors && image.colors.length > 0;
    const colorReference = hasImageColors && image.colors[0].colorReference;
    switch (processType) {
      case ProcessTypes.HotStamped:
      case ProcessTypes.FoilFusing:
        if (hasImageColors) {
          if (
            !itemUseScene7Template &&
            (image.type === userImageType.UploadLogo || image.type === userImageType.SavedArt)
          ) {
            url += '&op_texturize=hotstamp_' + image.colors[0].colorId;
          } else {
            url = getHotStampedScene7Url(image);
          }
          if (!extraParam?.noScl) url = addSclParameterToUrl(url, image);
        }
        break;
      case ProcessTypes.Deboss:
        if (!itemUseScene7Template) {
          const debossCode =
            fullPreview[currentImprintAreaKey] && fullPreview[currentImprintAreaKey][0].placeHolder.deboss.code;
          const bomOption = bomOptions && bomOptions.bomColors.find((o) => o.code === debossCode);
          if (bomOption) {
            const debossColorReference = bomOption.colorReference;
            if (debossColorReference)
              url += '&deboss=' + changeColorBrightness(debossColorReference, 5).replace('#', '') + ',100,000000,100';
          }
        } else {
          if (image.type === userImageType.UploadLogo) {
            url += getUploadLogoDefaultScene7TemplateUrl(image);
          }
          const colorizedColor = debossColorReference ? debossColorReference : '000000';
          url += '&op_contrast=-100&op_brightness=-100&op_colorize=' + colorizedColor;
        }
        break;
      default:
        if (isColorized || (!isColorized && image.type === userImageType.UploadLogo)) {
          if (itemUseScene7Template) {
            if (image.type === userImageType.UploadLogo) {
              url += getUploadLogoDefaultScene7TemplateUrl(image);
            }
          }
          if (isColorized) {
            if (colorReference)
              url += '&op_contrast=-100&op_brightness=-100&op_colorize=' + colorReference.replace('#', '');
          }
        }
        break;
    }

    // limit the resource image size if not UploadLogo
    if (!image.cropRect) {
      let previewDimension = getPreviewDimension(image.originalWidth, image.originalHeight);
      if (previewDimension.width > 0) url += '&wid=' + Math.round(previewDimension.width);
    }
    return url;
  }
);

export const getFullPreviewDict = createSelector(
  (state) => state.ui.fullPreviewDict,
  (fullPreviewDict) => fullPreviewDict
);

export const getUrlForImageCrop = createSelector(
  (_, image) => image,
  (state, image) => checkShowImageSingleColor(state, image),
  getItemUseScene7Template,
  (_, image, widthRequired) => widthRequired,
  (image, isColorized, itemUseScene7Template, widthRequired) => {
    if (!image) return '';

    let url = getBaseUrl(image, itemUseScene7Template);

    if (image.type === userImageType.SavedArt && !image.scene7FileName) {
      return image.thumbnail;
    }

    if (itemUseScene7Template) {
      if (image.type === userImageType.UploadLogo) {
        url += '&src=is{' + image.scene7FileName + '?size=' + image.originalWidth + ',' + image.originalHeight + '}';
        const scl = (image.originalWidth.width || image.originalWidth) / (widthRequired || MAX_CLOUD_IMAGE_SIZE);
        url += scl > 1 ? '&scl=' + scl : '&scl=1';
      }
    }
    const hasImageColors = image.colors && image.colors.length > 0;
    const colorReference = hasImageColors && image.colors[0].colorReference;
    if (isColorized) {
      if (colorReference) url += '&op_contrast=-100&op_brightness=-100&op_colorize=' + colorReference.replace('#', '');
    }

    // limit the resource image size if not UploadLogo
    if (!image.cropRect) {
      let previewDimension = getPreviewDimension(image.originalWidth, image.originalHeight);
      if (previewDimension.width > 0) url += '&wid=' + Math.round(previewDimension.width);
    }
    return url;
  }
);

export const getUrlForCanvasImage = createSelector(
  (_, image) => image,
  (state, image) => checkShowImageSingleColor(state, image),
  getItemUseScene7Template,
  (_, image, widthRequired) => widthRequired,
  (image, isColorized, itemUseScene7Template, widthRequired) => {
    if (!image) return '';

    let url = getBaseUrl(image, itemUseScene7Template);

    if (image.type === userImageType.SavedArt && !image.scene7FileName) {
      return image.thumbnail;
    }

    if (itemUseScene7Template) {
      if (image.type === userImageType.UploadLogo) {
        url +=
          '&src=is{' +
          image.scene7FileName +
          '?size=' +
          image.originalWidth +
          ',' +
          image.originalHeight +
          '}' +
          (image.finalCropRect
            ? '&crop=' +
              image.finalCropRect.x +
              ',' +
              image.finalCropRect.y +
              ',' +
              image.finalCropRect.width +
              ',' +
              image.finalCropRect.height
            : '');
        const scl = (image.finalCropRect.width || image.originalWidth) / (widthRequired || MAX_CLOUD_IMAGE_SIZE);
        url += scl > 1 ? '&scl=' + scl : '&scl=1';
      }
    }
    const hasImageColors = image.colors && image.colors.length > 0;
    const colorReference = hasImageColors && image.colors[0].colorReference;
    if (isColorized) {
      if (colorReference) url += '&op_contrast=-100&op_brightness=-100&op_colorize=' + colorReference.replace('#', '');
    }

    // limit the resource image size if not UploadLogo
    if (!image.cropRect) {
      let previewDimension = getPreviewDimension(image.originalWidth, image.originalHeight);
      if (previewDimension.width > 0) url += '&wid=' + Math.round(previewDimension.width);
    }
    return url;
  }
);

const getBaseUrl = (image, itemUseScene7Template) => {
  if (!image) return '';

  const PLACEHOLDER_SCENE7FILENAME = '{Scene7FileName}';
  let url = SCENE7_TEMPLATE_BASIC_URL_STATIC.replace(PLACEHOLDER_SCENE7FILENAME, image.scene7FileName);
  if (itemUseScene7Template) {
    if (image.type === userImageType.UploadLogo) url = SCENE7_URL_BASE;
    else if (image.type === userImageType.SavedArt)
      url = SCENE7_TEMPLATE_BASIC_URL_FOR_SAVEDART.replace(PLACEHOLDER_SCENE7FILENAME, image.scene7FileName);
  } else if (image.type === userImageType.UploadLogo || image.type === userImageType.SavedArt) {
    const ART_URL = `${
      nucleusHostName[process.env.REACT_APP_BUILD_ENV]
    }/api/v1/entity/Promo/content/{Scene7FileName}/image?`;
    url = ART_URL.replace(PLACEHOLDER_SCENE7FILENAME, image.scene7FileName);
  }

  return url;
};

const getUploadLogoDefaultScene7TemplateUrl = (imageData, widthRequired) => {
  const imageSize = { width: imageData.originalWidth, height: imageData.originalHeight };
  let url =
    '&src=is{' + imageData.scene7FileName + '?size=' + imageData.originalWidth + ',' + imageData.originalHeight + '}';
  return addSclParameterToUrl(url, imageData, widthRequired);
};

const addSclParameterToUrl = (url, imageData, widthRequired) => {
  const imageSize = { width: imageData.originalWidth, height: imageData.originalHeight };
  const scl = imageSize.width / (widthRequired || MAX_CLOUD_IMAGE_SIZE);
  url += scl > 1 ? '&scl=' + scl : '&scl=1';
  return url;
};
